import {
	useGetGcssCategoriesByIdsQuery,
	useGetGcssCategoryUpdatedAtAllQuery,
} from '@/api/sanity/generated';
import { createCache } from '@/utils/cache';
import { slugify } from '@/utils/string';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import type { CategoryListResponse } from '@/api/skippay/gcssCategories';

export type Slug = string;

export type Category = Omit<NonNullable<CategoryListResponse>[number], 'subcategories'> & {
	name: string;
	slug: Slug;
	description: MDXRemoteSerializeResult | null;
	image: string | null;
	subcategories: Array<{
		title: string;
		slug: Slug[];
		image: string | null;
	}>;
};

export type SubCategory = Category & {
	paths: Slug[];
};

type CachedCategory =
	| {
			kind: 'hit';
			description: MDXRemoteSerializeResult;
			image: string | null;
			updatedAt: string;
	  }
	| { kind: 'absent' };

const cache = createCache<CachedCategory, number>(30000, 'gcss-category-cache');

const BATCH_SIZE = 500;

let primePromise: Promise<void> | null = null;

export function primeGcssCategoryCache(ids: readonly number[]): Promise<void> {
	if (primePromise) {
		return primePromise;
	}

	primePromise = runPrime(ids).catch((err) => {
		primePromise = null;
		throw err;
	});

	return primePromise;
}

async function runPrime(ids: readonly number[]): Promise<void> {
	const updatedAtList = await useGetGcssCategoryUpdatedAtAllQuery.fetcher()();

	const sanityMeta = new Map<number, { id: string; updatedAt: string }>();
	for (const row of updatedAtList.allGcssCategory) {
		if (row._id && row.mappedCategory != null && typeof row._updatedAt === 'string') {
			sanityMeta.set(row.mappedCategory, { id: row._id, updatedAt: row._updatedAt });
		}
	}

	const mappedIdsToFetch: number[] = [];
	const sanityIdsToFetch: string[] = [];
	for (const id of ids) {
		const meta = sanityMeta.get(id);
		const cached = cache.get(id);

		if (!meta) {
			if (!cached || cached.kind !== 'absent') {
				cache.set(id, { kind: 'absent' });
			}
			continue;
		}

		if (!cached || cached.kind !== 'hit' || cached.updatedAt !== meta.updatedAt) {
			mappedIdsToFetch.push(id);
			sanityIdsToFetch.push(meta.id);
		}
	}

	if (sanityIdsToFetch.length === 0) {
		return;
	}

	const chunks: string[][] = [];
	for (let i = 0; i < sanityIdsToFetch.length; i += BATCH_SIZE) {
		chunks.push(sanityIdsToFetch.slice(i, i + BATCH_SIZE));
	}

	const results = await Promise.all(
		chunks.map((chunk) => useGetGcssCategoriesByIdsQuery.fetcher({ ids: chunk })())
	);

	const returnedIds = new Set<number>();
	const serializations: Array<Promise<void>> = [];

	for (const result of results) {
		for (const row of result.allGcssCategory) {
			if (row.mappedCategory == null || typeof row._updatedAt !== 'string') {
				continue;
			}

			const id = row.mappedCategory;
			returnedIds.add(id);

			if (!row.description) {
				throw new Error(`Category with id ${id} has no description`);
			}

			const source = row.description;
			const imageUrl = row.image?.asset?.url ?? null;
			const updatedAt = row._updatedAt;

			serializations.push(
				serialize(source).then((description) => {
					cache.set(id, { kind: 'hit', description, image: imageUrl, updatedAt });
				})
			);
		}
	}

	await Promise.all(serializations);

	for (const id of mappedIdsToFetch) {
		if (!returnedIds.has(id)) {
			cache.set(id, { kind: 'absent' });
		}
	}
}

export async function getCategoryDescriptionAndImage(id: number): Promise<{
	description: MDXRemoteSerializeResult;
	image: string | null;
} | null> {
	const cached = cache.get(id);

	if (cached && cached.kind === 'hit') {
		return { description: cached.description, image: cached.image };
	}

	return null;
}

export async function mapSubcategoriesWithDescriptionAndImage(
	category: {
		id: number;
		path: string[];
		subcategories: number[];
	},
	categoryMap: Map<number, Category['subcategories'][number]>
): Promise<Category['subcategories'][number][]> {
	const subcategories: Category['subcategories'][number][] = [];

	for (const subCategory of category.subcategories) {
		const relatedCategory = categoryMap.get(subCategory);

		if (relatedCategory) {
			const categorySetting = await getCategoryDescriptionAndImage(subCategory);

			const slug = [...category.path.map(slugify), ...relatedCategory.slug];
			subcategories.push({
				...relatedCategory,
				slug,
				image: categorySetting ? categorySetting.image : null,
			});
		}
	}

	return subcategories;
}
